/**
 * Election Period Detection and Safeguards
 * 
 * This utility helps detect election periods and apply additional safeguards
 * to protect the platform during high-risk periods.
 */

/**
 * Check if current date falls within an election period
 * 
 * Election periods in the Philippines typically include:
 * - Campaign period (usually 90 days before election)
 * - Election day
 * - Post-election period (30 days after for potential disputes)
 * 
 * @param currentDate Optional date to check (defaults to today)
 * @returns true if within election period, false otherwise
 */
export const isElectionPeriod = (currentDate: Date = new Date()): boolean => {
  const year = currentDate.getFullYear();
  
  // Major election years in the Philippines (adjust as needed)
  // National elections: 2025, 2028, 2031, etc. (every 3 years)
  // Local elections: 2025, 2028, 2031, etc. (synchronized with national)
  
  // Example: Check if we're in 2025 (election year)
  // You can expand this to check specific date ranges
  const electionYears = [2025, 2028, 2031, 2034, 2037];
  
  if (!electionYears.includes(year)) {
    return false;
  }
  
  // Check if we're in the campaign/election period
  // Campaign period typically starts 90 days before election day
  // For 2025: Election day is May 12, 2025
  // Campaign period: February 11 - May 12, 2025
  // Post-election period: May 12 - June 12, 2025
  
  const month = currentDate.getMonth() + 1; // 1-12
  const day = currentDate.getDate();
  
  if (year === 2025) {
    // Campaign period: Feb 11 - May 12
    if (month === 2 && day >= 11) return true;
    if (month >= 3 && month <= 4) return true;
    if (month === 5 && day <= 12) return true;
    // Post-election period: May 12 - June 12
    if (month === 5 && day > 12) return true;
    if (month === 6 && day <= 12) return true;
  }
  
  // Add other election years as needed
  // For now, return false if not in a known election period
  return false;
};

/**
 * Get election period warning message
 */
export const getElectionPeriodWarning = (): string => {
  return 'Election Period Active: This report is displayed during an active election period. VerifyPH operates as a neutral transparency platform and does not endorse or oppose any candidate. All content represents citizen-submitted observations, not official findings or legal determinations.';
};

/**
 * Check if a report requires additional scrutiny during election periods
 * 
 * @param reportCategory The category of the report
 * @param politicianName The politician name (if any)
 * @returns true if report requires extra scrutiny
 */
export const requiresElectionScrutiny = (
  reportCategory?: string,
  politicianName?: string
): boolean => {
  if (!isElectionPeriod()) {
    return false;
  }
  
  // During election periods, all reports with politician names need extra scrutiny
  if (politicianName) {
    return true;
  }
  
  // Anti-Epal reports are particularly sensitive during elections
  if (reportCategory === 'ANTI_EPAL') {
    return true;
  }
  
  return false;
};
